/*
  Infinite scroll for the collection grid.

  Watches a sentinel just below the last row and, when it comes into view,
  fetches the next page of the same section through Shopify's Section
  Rendering API and appends its cards. The numbered pagination stays in the
  markup as the no-JS fallback and is hidden once this takes over.

  facets.js replaces #ProductGridContainer wholesale when a filter or sort
  changes, which re-creates this element, so it re-initialises on its own with
  the filtered page 1.
*/
if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.grid = document.getElementById('product-grid');
        this.sentinel = this.querySelector('[data-infinite-sentinel]');
        this.status = this.querySelector('[data-infinite-status]');
        this.pagination = this.closest('.collection')?.querySelector('.pagination-wrapper');
        this.nextUrl = this.dataset.nextUrl || '';
        this.loading = false;

        if (!this.grid || !this.sentinel) return;

        // Nothing more to fetch, or the browser can't observe: leave the
        // numbered pagination in place and don't spin a spinner forever.
        if (!this.nextUrl || !('IntersectionObserver' in window)) {
          this.sentinel.hidden = true;
          return;
        }

        // Only take over once we know we can do the job.
        if (this.pagination) this.pagination.hidden = true;

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) this.loadNext();
          },
          // Start fetching while the last rows are still on screen.
          { rootMargin: '600px 0px' }
        );
        this.observer.observe(this.sentinel);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      finish() {
        if (this.observer) this.observer.disconnect();
        this.sentinel.hidden = true;
      }

      async loadNext() {
        if (this.loading || !this.nextUrl) return;
        this.loading = true;
        this.sentinel.setAttribute('aria-busy', 'true');

        try {
          const url = new URL(this.nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);

          const response = await fetch(url.toString());
          if (!response.ok) throw new Error(response.status);

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const items = doc.querySelectorAll('#product-grid > li');
          if (!items.length) return this.finish();

          items.forEach((item) => {
            const card = document.importNode(item, true);
            // Entrance animations are keyed to the first page's order; without
            // this the appended rows start invisible.
            card.classList.remove('scroll-trigger', 'animate--slide-in');
            card.removeAttribute('data-cascade');
            card.style.removeProperty('--animation-order');
            this.grid.appendChild(card);
          });

          const nextLink = doc.querySelector('[data-infinite-scroll]');
          this.nextUrl = (nextLink && nextLink.dataset.nextUrl) || '';
          if (this.status) this.status.textContent = `${items.length} more products loaded`;
          if (!this.nextUrl) this.finish();
        } catch (error) {
          console.error('Infinite scroll failed:', error);
          // Hand control back to the numbered pagination rather than stranding
          // the visitor part-way through the collection.
          if (this.pagination) this.pagination.hidden = false;
          this.finish();
        } finally {
          this.loading = false;
          this.sentinel.removeAttribute('aria-busy');
        }
      }
    }
  );
}
